import { useState } from "react";

import EditMentionModal from "@/containers/EditMentionModal";
import { useMentionCard } from "@/containers/MentionCard/context";
import ExternalLink from "@/containers/MentionCard/partials/Body/partials/ExternalLink";
import Actions from "@/containers/MentionCard/partials/Mention/Actions";
import RemoveMention from "@/containers/MentionCard/partials/Mention/RemoveMention";
import Similar from "@/containers/MentionCard/partials/Mention/Similar";
import Topic from "@/containers/MentionCard/partials/Mention/Topic";
import Translate from "@/containers/MentionCard/partials/Translate";
import { useI18n } from "@/contexts/i18nContext";
import { useMentionCards } from "@/contexts/MentionCardsContext";
import { MentionV4 } from "@/types/MentionCard";
import { cx } from "@/utils";
import AccessControl, { ACCESS_LEVELS } from "@components/AccessControl";
import Typography from "@components/Typography";
import UndoRemovedMention from "@components/UndoRemovedMention";

import styles from "./Mention.module.css";

const Mention: React.FunctionComponent<{
  mention: MentionV4;
  hideActions?: boolean;
  hideMetadata?: boolean;
  hideExternalLink?: boolean;
  className?: string;
}> = ({
  className,
  hideExternalLink,
  mention,
  hideMetadata: forceHideMetadata,
  hideActions: forceHideActions,
}) => {
  const {
    hideActions,
    hideMetadata,
    hideSimilar,
    mentions,
    removedMentionIds,
    channel,
    topic_names,
    episode: { pk: episode_pk },
  } = useMentionCard();
  const { language } = useI18n();
  const { updateMention } = useMentionCards();
  const [editing, setEditing] = useState(false);
  const allMentionsRemoved = removedMentionIds.length === mentions.length;
  const isRemoved = removedMentionIds.includes(mention.id);

  const channel_language = channel.language_guess || channel.language;
  const channelNotInUserLanguage = channel_language !== language;

  return (
    <>
      <div
        className={cx(styles.root, "mention", className)}
        data-mention-id={mention.id}
      >
        <div>
          <Typography
            data-testid="mention-text-content"
            className={cx(
              { [styles.lineThrough]: isRemoved },
              "highlight-match"
            )}
            component="span"
            dangerouslySetInnerHTML={{ __html: mention?.trimmed_content }}
          />
          {isRemoved && !allMentionsRemoved ? (
            <UndoRemovedMention currentId={mention.id} />
          ) : (
            <div className={styles.actions}>
              {!forceHideMetadata && !hideMetadata && (
                <Topic topic_names={topic_names} />
              )}
              {channelNotInUserLanguage && !hideActions && (
                <Translate mentions={[mention]} toKey="trimmed_content" />
              )}
              {!hideExternalLink && <ExternalLink />}
              {!forceHideActions && !hideActions && (
                <AccessControl level={ACCESS_LEVELS.USER}>
                  <RemoveMention currentId={mention.id} />
                  <Actions mention={mention} setEditing={setEditing} />
                </AccessControl>
              )}
            </div>
          )}
          {!hideSimilar && mention.similar_mentions?.length > 0 && (
            <Similar
              mentions={mention.similar_mentions}
              episode_pk={episode_pk}
            />
          )}
        </div>
      </div>
      <EditMentionModal
        show={editing}
        onHide={() => setEditing(false)}
        mention={mention}
        updateMention={updateMention}
      />
    </>
  );
};

export default Mention;
